import { motion } from "framer-motion";
import { Sparkles, HeartHandshake, Users } from "lucide-react";

const STATS = [
  { icon: Sparkles, label: "Est. 2023", sub: "Started at age 13" },
  { icon: Users, label: "4-5 people", sub: "Fed by one Nacho Box" },
  { icon: HeartHandshake, label: "3 event types", sub: "Vendor • School • Birthday" },
];

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Story() {
  return (
    <section id="story" data-testid="story-section" className="relative overflow-hidden bg-cream py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="relative mx-auto w-full max-w-md"
        >
          <motion.div custom={0} variants={reveal} className="rotate-[-3deg] rounded-3xl border-4 border-ink bg-white p-3 shadow-popLg">
            <img
              src="/brand/founder-portrait.jpg"
              alt="Sy Sy — founder of Sy Sy's Snacks — smiling with arms crossed in his senior portrait"
              className="h-80 w-full rounded-2xl object-cover object-top sm:h-96"
            />
            <span className="font-display block px-2 pb-1 pt-2.5 text-center text-sm font-semibold uppercase tracking-widest text-ink/70">
              Sy Sy, founder, senior year
            </span>
          </motion.div>

          <motion.div
            custom={1}
            variants={reveal}
            className="absolute -top-12 -left-2 w-32 rotate-[-8deg] rounded-2xl border-4 border-ink bg-white p-3 shadow-pop sm:-left-8 sm:w-36"
          >
            <img
              src="/brand/logo-mark.png"
              alt="Sy Sy's Snacks logo mark"
              className="h-24 w-full rounded-xl object-contain sm:h-28"
            />
          </motion.div>
        </motion.div>

        <div>
          <motion.p
            custom={0}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-sm font-black uppercase tracking-[0.24em] text-tang"
          >
            Our Story
          </motion.p>
          <motion.h2
            custom={1}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display mt-4 text-4xl font-bold leading-tight sm:text-5xl"
          >
            Inspiring Dreams,
            <br />
            <span className="font-logo text-tomato">Celebrating Smiles</span>
          </motion.h2>

          <motion.div
            custom={2}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-7 space-y-5 text-base font-semibold leading-relaxed text-ink/80 sm:text-lg"
          >
            <p>
              Sy Sy's Snacks started in <span className="bg-sunburst px-1.5 font-black text-ink">2023 when I was 13 years old</span>.
              Today I'm a senior in high school, and I'm still rolling up to events with the same
              dream: great snacks that don't break the bank.
            </p>
            <p>
              I realized that sometimes families can't afford multiple meals at a game. So I created
              something affordable: <span className="font-black text-tang">the famous Nacho Box</span>,
              built to feed 4-5 people: pastor or no meat, loaded with nacho cheese, sour cream,
              pinto beans, guacamole, jalapeños, olives and pico de gallo.
            </p>
            <p>
              When you support Sy Sy's Snacks, you're not just enjoying awesome treats, you're
              helping a young entrepreneur inspire other young people to chase their dreams too.
            </p>
          </motion.div>

          <motion.div
            custom={3}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-3"
          >
            {STATS.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border-[3px] border-ink bg-white p-4 shadow-pop transition-transform duration-200 hover:-translate-y-1"
              >
                <s.icon className="h-6 w-6 text-tomato" strokeWidth={2.5} />
                <p className="font-display mt-2 text-xl font-bold">{s.label}</p>
                <p className="text-xs font-bold uppercase tracking-wide text-ink/60">{s.sub}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
